import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { SEARCH_DEFAULTS, SEARCH_KEYBOARD, SEARCH_PATHS } from '../constants/search'
import { buildSearchPath } from '../helpers/search'
import { useProductSearchSuggestions } from './useProductSearchSuggestions'

export function useHeaderSearch() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const urlQuery =
    pathname === SEARCH_PATHS.page ? (searchParams.get('q') ?? '') : ''

  const [query, setQuery] = useState(urlQuery)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const searchRef = useRef<HTMLFormElement>(null)
  const trimmedQuery = query.trim()
  const suggestionsEnabled =
    panelOpen && trimmedQuery.length >= SEARCH_DEFAULTS.minQueryLength
  const suggestions = useProductSearchSuggestions(query, suggestionsEnabled)

  useEffect(() => {
    if (pathname === SEARCH_PATHS.page) {
      setQuery(searchParams.get('q') ?? '')
    }
  }, [pathname, searchParams])

  useEffect(() => {
    setPanelOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!panelOpen) return

    function onPointerDown(event: PointerEvent) {
      if (!searchRef.current?.contains(event.target as Node)) {
        setPanelOpen(false)
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === SEARCH_KEYBOARD.escape) {
        setPanelOpen(false)
      }
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [panelOpen])

  function closeMobile() {
    setMobileOpen(false)
  }

  function closePanel() {
    setPanelOpen(false)
  }

  function onQueryChange(value: string) {
    setQuery(value)
    setPanelOpen(value.trim().length >= SEARCH_DEFAULTS.minQueryLength)
  }

  function onSearchFocus() {
    if (trimmedQuery.length >= SEARCH_DEFAULTS.minQueryLength) {
      setPanelOpen(true)
    }
  }

  function onSearchSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!trimmedQuery) return
    closeMobile()
    closePanel()
    navigate(buildSearchPath({ q: trimmedQuery }))
  }

  function toggleMobile() {
    setMobileOpen((open) => !open)
  }

  return {
    query,
    onQueryChange,
    onSearchFocus,
    mobileOpen,
    onSearchSubmit,
    toggleMobile,
    closeMobile,
    searchRef,
    panelOpen: suggestionsEnabled,
    suggestions: suggestions.products,
    suggestionsLoading: suggestions.loading,
    suggestionsError: suggestions.error,
    closePanel,
  }
}
